import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Dropdown, Button, Avatar } from 'antd';
import { DashboardOutlined, TrophyOutlined, FileTextOutlined, LogoutOutlined, SafetyOutlined, UserOutlined, BankOutlined, DownOutlined, CheckOutlined, FileSearchOutlined, CalendarOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { exportToCSV } from '../../helpers/exportHelpers';
import { getUserInitials } from '../../helpers/userHelpers';
import { exportLeadsWithFilters } from '../../services/zohoService';
import { getAllCompanies } from '../../services/companyService';
import { extractCompanyNames } from '../../helpers/companyHelpers';
import { getAllowedCompanies, hasPermission } from '../../utils/roleHelpers';
import { AVAILABLE_STATUSES } from '../../utils/constants';
import { useMessageApi } from '../../shared/providers/MessageProvider';
import Logo from '../../assets/images/logo-colored.png';
import MenuIcon from '../../assets/images/menu-icon.svg';
import ReportsModal from '../ReportsModal/ReportsModal';
import ExportModal from '../../shared/components/ExportModal/ExportModal';
import ExportIcon from '../../assets/icons/export-icon.svg';
import './Navbar.scss';

const Navbar = ({ 
  isAdmin,
  isSuperAdmin = false,
  company, 
  onCompanySelect, 
  merchants = [], 
  headers = [], 
  currentFilters = {},
  isLoading = false,
  userInfo = null,
  permissions = {},
  canViewAllCompanies = false
}) => {
  // Use Auth0 React SDK hook
  const { logout } = useAuth0();
  
  // Message API from MessageProvider for toast notifications
  const message = useMessageApi();
  
  // Local state for companies
  const [companies, setCompanies] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Use React Router hooks to get navigation and location
  // This allows us to navigate programmatically and access the current path
  const navigate = useNavigate();
  const location = useLocation();

  // State for Reports Modal
  const [showReportsModal, setShowReportsModal] = useState(false);
  
  // State for CSV Export Modal
  const [showExportModal, setShowExportModal] = useState(false);

  // Fetch companies when component mounts - optimized with cleanup
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        // Fetch all companies without pagination
        const result = await getAllCompanies({}, message);
        
        // Extract companies array from result
        const companiesArray = result?.companies || [];
        
        // Extract company names from company objects
        const companiesData = extractCompanyNames(companiesArray);
        
        // Only update state if component is still mounted
        if (isMounted) {
          setCompanies(companiesData || []);
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        if (isMounted) {
          setCompanies([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
        }
      }
    };

    fetchData();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [message]); // Only run on mount or when message changes

  // Get allowed companies based on user permissions
  const allowedCompanies = useMemo(() => 
    getAllowedCompanies(userInfo, companies),
    [userInfo, companies]
  );

  // Function to get page title based on current path
  const getPageTitle = (location, navigate) => {
    const path = location.pathname;

    // Define static routes
    const pageTitles = {
      "/dashboard": "Dashboard",
      "/dashboard/companies": "Companies",
      "/dashboard/reports": "Reports",
      "/dashboard/incentive": "Incentive Program",
      "/dashboard/users": "Users",
      "/dashboard/roles": "Role & Permission Management",
      "/dashboard/rate-review": "Rate Review",
      "/dashboard/admin-booking-config": "Admin Booking & Routing"
    };

    const title = pageTitles[path] || "Dashboard";

    return title;
  };

  // Navigation helper function
  // This function will be used to navigate to any given path
  const handleNavigate = (path) => {
    navigate(path);
  };

  // Handle Reports Modal
  const handleReportsClick = () => {
    setShowReportsModal(true);
  };

  // Handle Company Selection - same as MerchantFilter
  const handleCompanyChange = (companyName) => {
    if (onCompanySelect) {
      onCompanySelect(companyName);
    }
  };

  // Check permissions for menu items and export - MUST be defined before using
  // Using hasPermission helper for consistency with Dashboard.jsx
  const canViewDashboard = hasPermission(userInfo, 'dashboard', 'view');
  const canViewCompanies = hasPermission(userInfo, 'companies', 'view');
  const canViewIncentive = hasPermission(userInfo, 'incentive', 'view');
  const canViewReports = hasPermission(userInfo, 'reports', 'view');
  const canViewUsers = hasPermission(userInfo, 'users', 'view');
  const canViewRolesAndPermissions = hasPermission(userInfo, 'roles_and_permissions', 'view');
  const canViewRateReview = hasPermission(userInfo, 'rate_review_portal', 'view');
  const canViewAdminBookingConfig = hasPermission(userInfo, 'admin_booking_config', 'view');
  const canExportCSV = hasPermission(userInfo, 'dashboard', 'export_csv');

  // Handle CSV Export functionality
  const handleExportClick = () => {
    if (!isLoading && merchants.length > 0 && canExportCSV) {
      setShowExportModal(true);
    }
  };

  // Handle export from modal
  const handleExportFromModal = async (exportType, filters) => {
    if (exportType === 'all') {
      // Export all current data on page
      exportToCSV(merchants, headers);
    } else {
      // Export with filters - error handling done in zohoService
      await exportLeadsWithFilters(company, filters);
    }
  };

  // Get available options for filters
  const availableStatuses = AVAILABLE_STATUSES;

  // Check if export button should be disabled
  const exportButtonTitle = !canExportCSV
    ? 'You do not have permission to export CSV'
    : isLoading 
    ? 'Loading...' 
    : merchants.length === 0 
    ? 'No data to export' 
    : 'Export to CSV';

  // Get current active route
  const getActiveKey = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path === '/dashboard/companies') return 'companies';
    if (path === '/dashboard/incentive') return 'incentive';
    if (path === '/dashboard/reports') return 'reports';
    if (path === '/dashboard/users') return 'users';
    if (path === '/dashboard/roles') return 'roles';
    if (path === '/dashboard/rate-review') return 'rate-review';
    if (path === '/dashboard/admin-booking-config') return 'admin-booking-config';
    return '';
  };

  // Check if we're on the Users or Roles & Permissions route
  const isOnAdminRoute = location.pathname === '/dashboard/users' || 
                         location.pathname.startsWith('/dashboard/users/') ||
                         location.pathname === '/dashboard/roles' ||
                         location.pathname.startsWith('/dashboard/roles/') ||
                         location.pathname === '/dashboard/admin-booking-config' ||
                         location.pathname.startsWith('/dashboard/admin-booking-config/')

  // Check if we're on the Dashboard page
  const isOnDashboardPage = location.pathname === '/dashboard';

  // Get user display name and initials
  const userName = userInfo?.name || 'User';
  const userEmail = userInfo?.email || '';
  const userInitials = getUserInitials(userName);
  const userRole = userInfo?.role || '';
  const userCompany = userInfo?.company || '';

  // Avatar dropdown items - Simple and modern
  const avatarDropdownItems = [
    // User Profile Info
    {
      key: 'user-name',
      label: (
        <div className="user-profile-simple">
          <div className="avatar-wrapper">
            <Avatar 
              size={44} 
              className="user-avatar-simple"
            >
              {userInitials}
            </Avatar>
            <span className="online-status-badge"></span>
          </div>
          <div className="user-info-simple">
            <div className="user-name-simple">{userName}</div>
            {userEmail && (
              <div className="user-email-simple">{userEmail}</div>
            )}
            {userCompany && (
              <div className="user-company-simple">
                <BankOutlined />
                <span>{userCompany}</span>
              </div>
            )}
            {userRole && (
              <div className="user-role-simple">{userRole}</div>
            )}
          </div>
        </div>
      ),
      disabled: true,
      className: 'user-profile-item-simple',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: () => logout({ 
        logoutParams: { 
          returnTo: process.env.REACT_APP_AUTH0_RETURN_URL || window.location.origin 
        } 
      }),
      danger: true,
    },
  ];

  // Company dropdown items - modern dropdown menu
  const companyDropdownItems = useMemo(() => {
    if (!allowedCompanies || allowedCompanies.length === 0) {
      return [];
    }

    return allowedCompanies.map((companyName) => ({
      key: companyName,
      label: (
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: '8px'
          }}
        >
          <span style={{ flex: 1 }}>{companyName}</span>
          {company === companyName && (
            <CheckOutlined 
              style={{ 
                color: '#2E8534', 
                fontSize: '14px',
                fontWeight: 'bold'
              }} 
            />
          )}
        </div>
      ),
      onClick: () => handleCompanyChange(companyName),
      className: company === companyName ? 'company-item-active' : '',
    }));
  }, [allowedCompanies, company, handleCompanyChange]);

  // Menu items for Ant Design dropdown - filtered by permissions (Navigation items only)
  const menuItems = [
    // Dashboard - show only if user has view permission
    ...(canViewDashboard ? [{
      key: 'dashboard',
      label: (
        <Link 
          to="/dashboard" 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <DashboardOutlined />
          Dashboard
        </Link>
      ),
      className: getActiveKey() === 'dashboard' ? 'menu-item-active' : '',
    }] : []),
    // Companies - show only if user has view permission
    ...(canViewCompanies ? [{
      key: 'companies',
      label: (
        <Link 
          to="/dashboard/companies" 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <BankOutlined />
          Companies
        </Link>
      ),
      className: getActiveKey() === 'companies' ? 'menu-item-active' : '',
    }] : []),
    // Incentive - show only if user has view permission
    ...(canViewIncentive ? [{
      key: 'incentive',
      label: (
        <Link 
          to={`/dashboard/incentive?company=${company}`} 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <TrophyOutlined />
          Incentive Program
        </Link>
      ),
      className: getActiveKey() === 'incentive' ? 'menu-item-active' : '',
    }] : []),
    // Reports - show only if user has view permission
    ...(canViewReports ? [{
      key: 'reports',
      label: (
        <Link 
          to="/dashboard/reports" 
          onClick={(e) => {
            e.preventDefault();
            handleReportsClick();
          }}
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <FileTextOutlined />
          Reports
        </Link>
      ),
      className: getActiveKey() === 'reports' ? 'menu-item-active' : '',
    }] : []),
    // Users - show only if user has users view permission
    ...(canViewUsers ? [{
      key: 'users',
      label: (
        <Link 
          to="/dashboard/users" 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <UserOutlined />
          Users
        </Link>
      ),
      className: getActiveKey() === 'users' ? 'menu-item-active' : '',
    }] : []),
    // Role & Permission - show only if user has roles_and_permissions.view permission
    ...(canViewRolesAndPermissions ? [{
      key: 'roles',
      label: (
        <Link 
          to="/dashboard/roles" 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <SafetyOutlined />
          Role & Permission
        </Link>
      ),
      className: getActiveKey() === 'roles' ? 'menu-item-active' : '',
    }] : []),
    // Rate Review - show only if user has rate_review_portal.view permission
    ...(canViewRateReview ? [{
      key: 'rate-review',
      label: (
        <Link 
          to="/dashboard/rate-review" 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <FileSearchOutlined />
          Rate Review
        </Link>
      ),
      className: getActiveKey() === 'rate-review' ? 'menu-item-active' : '',
    }] : []),
    // Admin Booking & Routing - show only if user has admin_booking_config.view permission
    ...(canViewAdminBookingConfig ? [{
      key: 'admin-booking-config',
      label: (
        <Link 
          to="/dashboard/admin-booking-config" 
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <CalendarOutlined />
          Admin Booking & Routing
        </Link>
      ),
      className: getActiveKey() === 'admin-booking-config' ? 'menu-item-active' : '',
    }] : []),
  ];

  return (
    <header className="db-header">
      <div className="container-fluid">
        <div className="row align-items-center">
          {/* Left section: Logo and page title */}
          <div className="col-7 d-flex align-items-center">
            {/* Clicking the logo will take you to the dashboard page */}
            <Link
              to="/dashboard"
              aria-label="Go to Dashboard"
            >
              <img src={Logo} alt="Company Logo" />
            </Link>

            {/* Dynamic page heading with export button */}
            <div className="d-flex align-items-center gap-3">
              <h1 className="page-heading mb-0">
                {getPageTitle(location, navigate)}
              </h1>
              {/* CSV Export button - only shown on Dashboard page if user has export permission */}

              {canViewDashboard && isOnDashboardPage && canExportCSV && (
                <img
                  src={ExportIcon}
                  alt="Export Icon"
                  title={exportButtonTitle}
                  onClick={handleExportClick}
                  style={{ width: "20px", height: "20px", cursor: "pointer" }}
                />
              )}
            </div>
          </div>

          {/* Right section: Company dropdown, Avatar dropdown and Menu dropdown */}
          <div className="col-5 text-end d-flex align-items-center justify-content-end gap-3">
            {/* Company Dropdown - Modern Dropdown Menu - show only if user can view all companies and not on admin routes and on Dashboard page */}
            {canViewAllCompanies && !isOnAdminRoute && isOnDashboardPage && allowedCompanies.length > 0 && (
              <Dropdown
                menu={{ 
                  items: companyDropdownItems,
                  style: { maxHeight: '300px', overflowY: 'auto' }
                }}
                trigger={['click']}
                placement="bottomRight"
                loading={isLoadingData}
              >
                <Button
                  className="company-dropdown-button"
                  type="default"
                  size="large"
                  loading={isLoadingData}
                >
                  <BankOutlined style={{ marginRight: '8px', color: '#2E8534' }} />
                  <span className="company-button-text">
                    {company || 'Select Company'}
                  </span>
                  <DownOutlined 
                    style={{ 
                      marginLeft: '8px', 
                      fontSize: '11px',
                      color: '#2E8534',
                      transition: 'transform 0.3s ease'
                    }} 
                  />
                </Button>
              </Dropdown>
            )}

            {/* Menu Dropdown - Navigation Items Only */}
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                className="menu-button-antd"
                type="default"
                size="large"
              >
                <img src={MenuIcon} alt="Menu Icon" />
              </Button>
            </Dropdown>

            {/* Avatar Dropdown - User Profile Info and Logout */}
            <Dropdown
              menu={{ items: avatarDropdownItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                className="avatar-button-antd"
                type="default"
                shape="circle"
                size="large"
              >
                <Avatar 
                  size={32}
                  className="navbar-avatar"
                >
                  {userInitials}
                </Avatar>
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>
      
      {/* Reports Modal */}
      <ReportsModal 
        show={showReportsModal}
        onHide={() => setShowReportsModal(false)}
      />
      
      {/* CSV Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportFromModal}
        company={company}
        availableStatuses={availableStatuses}
      />
    </header>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(Navbar);
